"use client";
import React, { useState } from "react";
import Link from "next/link";
import { ThemeSwitcher } from "@shared/src/components/shared/ThemeSwitcher";
import { IconButton, MenuItem } from "./primitives";
import { RiDropFill } from "react-icons/ri";
import { GoldBarsIcon, ResetIcon } from "@shared/src/components/assets/icons";
import { IS_MAINNET } from "@shared/src/constants";
import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { FaucetModal } from "@shared/src/components/common/dialogs/FaucetModal";
import { CreateAdButton } from "@shared/src/components/common/buttons/CreateAdButton";
import { FaPowerOff } from "react-icons/fa";
import { menuNavigation, navigationConfig, drawerNavigation, type NavigationItem } from "@shared/src/config/navigation";
import { Button } from "@shared/src/components/ui/button";
import { MdKeyboardArrowDown } from "react-icons/md";
import { CustomDropdown } from "./CustomDropdown";
import { cn } from "@shared/src/utils/cn";

export function Menu({ onCloseParentDropdown }: { onCloseParentDropdown?: () => void }) {
  const [openInnerDropdown, setOpenInnerDropdown] = useState<string | null>(null);
  const [isFaucetOpen, setIsFaucetOpen] = useState(false);
  const signOut = useAuthStore(state => state.signOut);

  const handleMenuItemClick = () => {
    setOpenInnerDropdown(null);
    onCloseParentDropdown?.();
  };

  const handleFaucetClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFaucetOpen(true);
  };

  async function handleSignOut() {
    signOut();
    setOpenInnerDropdown(null);
    onCloseParentDropdown?.();
  }

  return (
    <div className="min-w-[200px] space-y-4">
      <div className="flex justify-between items-center pt-2">
        <div className="lg:hidden flex items-center gap-2 mr-2">
          {!IS_MAINNET && (
            <IconButton onClick={handleFaucetClick} className="bg-surfaces-bg-secondary-darker">
              <RiDropFill size={20} className="text-text-secondary" />
              <span>Faucet</span>
            </IconButton>
          )}
          <Link href="/gold-bars">
            <IconButton size="sm" className="text-warning-500 text-small-2 bg-surfaces-bg-secondary-darker">
              <GoldBarsIcon size={20} />
            </IconButton>
          </Link>
        </div>
        <ThemeSwitcher className="bg-surfaces-bg-secondary-darker" />
      </div>
      <div className="h-px w-full bg-surfaces-bg-tertiary" />

      <div className="space-y-2">
        {[
          ...menuNavigation,
          { href: "#", label: "Refresh", icon: ResetIcon, onClick: () => window?.location.reload() },
          { href: "##", label: "Sign Out", icon: FaPowerOff, onClick: handleSignOut },
        ].map((item, index, array) => (
          <div key={item.href}>
            {index === array.length - 2 && <div className="h-px w-full bg-border-tertiary my-2" />}
            <MenuItem
              href={item.href}
              icon={item.icon}
              label={item.label}
              onSelect={item.onClick || handleMenuItemClick}
            />
          </div>
        ))}
      </div>

      <div className="pb-2 lg:hidden">
        <div className="h-px w-full bg-surfaces-bg-tertiary mb-4" />
        <CreateAdButton className="w-full" />
      </div>

      <FaucetModal open={isFaucetOpen} onOpenChange={setIsFaucetOpen} />
    </div>
  );
}

export function LogoDropdown({ onCloseParentDropdown }: { onCloseParentDropdown?: () => void }) {
  const [openInnerDropdown, setOpenInnerDropdown] = useState<string | null>(null);

  const handleMenuItemClick = () => {
    setOpenInnerDropdown(null);
    onCloseParentDropdown?.();
  };

  return (
    <div className="min-w-[200px] space-y-4">
      <div className="max-navbar-bp:grid max-navbar-bp:grid-cols-2 max-navbar-bp:gap-4">
        <div className="max-navbar-bp:block hidden space-y-2">
          {navigationConfig.slice(3).map((item: NavigationItem) => {
            if (item.href === "/help" && item.type === "dropdown") {
              const dropdownId = `inner-dropdown-${item.label.toLowerCase()}`;
              const isOpen = openInnerDropdown === dropdownId;
              return (
                <CustomDropdown
                  key={item.href}
                  trigger={
                    <Button variant="ghost" size="md" width="full" className="flex items-center gap-3 justify-between">
                      <div className="flex items-center gap-3">
                        <item.icon size={20} />
                        <span className="text-text-primary">{item.label}</span>
                      </div>
                      <MdKeyboardArrowDown
                        size={20}
                        className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </Button>
                  }
                  content={
                    <div className="p-4">
                      {item.children?.map(child => (
                        <Link
                          key={child.label}
                          href={child.href}
                          className="block px-4 py-2 text-text-secondary hover:text-text-primary"
                          onClick={() => {
                            setOpenInnerDropdown(null);
                            onCloseParentDropdown?.();
                          }}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  }
                  dropdownId={dropdownId}
                  openDropdown={openInnerDropdown}
                  setOpenDropdown={setOpenInnerDropdown}
                />
              );
            }
            return null;
          })}
        </div>
        <div className="space-y-2">
          {drawerNavigation.other.map(item => (
            <MenuItem
              key={item.href}
              href={item.href}
              icon={item.icon}
              label={item.label}
              onSelect={handleMenuItemClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
